import React, { useEffect, useState, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Divider from '@mui/material/Divider';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ExploreIcon from '@mui/icons-material/Explore';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { toDisplayName } from '../../utils';
import { Perspective } from '../../types';

// ============================================================
// Search scoring
// ============================================================

function scorePerspective(dm: Perspective, queryTerms: string[], domainNameMap: Record<string, string> | null): number {
  if (queryTerms.length === 0) return 0;
  const corpus = [
    dm.name,
    dm.description,
    ...(dm.keyMetrics || []),
    ...(dm.keyDimensions || []),
    // the domain's name is a search term only while the domain layer is on (#464)
    ...(domainNameMap ? [domainNameMap[dm.domain] || dm.domain] : []),
  ]
    .join(' ')
    .toLowerCase();

  let matched = 0;
  for (const term of queryTerms) {
    if (corpus.includes(term)) matched++;
  }
  return matched / queryTerms.length;
}

// ============================================================
// BrowsePage
// ============================================================

export default function BrowsePage({ find, onFind }: { find?: string; onFind?: (value: string) => void } = {}) {
  const domains = useVoyagerStore((s) => s.domains);
  const allPerspectives = useVoyagerStore((s) => s.allPerspectives);
  const pinnedPerspectives = useVoyagerStore((s) => s.pinnedPerspectives);
  const fetchDomains = useVoyagerStore((s) => s.fetchDomains);
  const fetchAllPerspectives = useVoyagerStore((s) => s.fetchAllPerspectives);
  const navigateToWorkspace = useVoyagerStore((s) => s.navigateToWorkspace);
  const togglePinPerspective = useVoyagerStore((s) => s.togglePinPerspective);
  // Without the domain layer (#464): one flat grid, by name, no domain chips.
  const domainsOn = useVoyagerStore((s) => s.features.domains);

  const [localFind, setLocalFind] = useState('');
  const searchQuery = find ?? localFind;
  const setSearchQuery = onFind ?? setLocalFind;
  const [popperOpen, setPopperOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDomains();
    fetchAllPerspectives();
  }, [fetchDomains, fetchAllPerspectives]);

  const domainNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of domains) {
      map[d.id] = d.name;
    }
    return map;
  }, [domains]);

  const suggestions = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return [];
    const terms = trimmed.split(/\s+/);
    return allPerspectives
      .map((dm) => ({ dm, score: scorePerspective(dm, terms, domainsOn ? domainNameMap : null) }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((s) => s.dm);
  }, [searchQuery, allPerspectives, domainNameMap, domainsOn]);

  const perspectivesByDomain = useMemo(() => {
    const map: Record<string, Perspective[]> = {};
    if (!domainsOn) {
      map[''] = [...allPerspectives].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      return map;
    }
    for (const dm of allPerspectives) {
      if (!map[dm.domain]) map[dm.domain] = [];
      map[dm.domain].push(dm);
    }
    return map;
  }, [allPerspectives, domainsOn]);

  const domainLabel = (domainId: string) => (domainsOn ? (domainNameMap[domainId] || domainId) : undefined);

  const pinnedDms = useMemo(
    () => allPerspectives.filter((dm) => pinnedPerspectives.includes(dm.id)),
    [allPerspectives, pinnedPerspectives]
  );

  const handleSelectSuggestion = (dm: Perspective) => {
    setSearchQuery('');
    setPopperOpen(false);
    navigateToWorkspace(dm.domain, dm.id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* ── Hero: natural height, horizontally centered ── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 6,
          pb: 4,
          px: 3,
          flexShrink: 0,
        }}
      >
        {/* Branding — same pattern as LandingPage */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <ExploreIcon sx={{ color: 'primary.main', fontSize: 44 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Browse Perspectives
          </Typography>
        </Box>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 4, fontWeight: 400, textAlign: 'center' }}
        >
          {domainsOn
            ? 'Search or browse by domain to open a dataset in the Query Builder.'
            : 'Search or browse to open a dataset in the Query Builder.'}
        </Typography>

        {/* Search bar — same Paper style as LandingInput */}
        <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
          <Box sx={{ width: '100%', maxWidth: 720, position: 'relative' }} ref={anchorRef}>
            <Paper
              elevation={3}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <SearchIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
              <TextField
                inputRef={inputRef}
                variant="standard"
                fullWidth
                placeholder="Search perspectives, metrics, dimensions..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPopperOpen(e.target.value.trim().length > 0);
                }}
                onFocus={() => {
                  if (searchQuery.trim().length > 0) setPopperOpen(true);
                }}
                InputProps={{ disableUnderline: true }}
                sx={{ '& input': { fontSize: 15, lineHeight: 1.5 } }}
              />
            </Paper>

            <Popper
              open={popperOpen && suggestions.length > 0}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
            >
              <Paper elevation={4} sx={{ mt: 0.5, overflow: 'hidden' }}>
                {suggestions.map((dm) => (
                  <Box
                    key={dm.id}
                    onClick={() => handleSelectSuggestion(dm)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {dm.name}
                      </Typography>
                      {domainsOn && (
                        <Chip
                          label={domainNameMap[dm.domain] || dm.domain}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Box>
                    {dm.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 12,
                        }}
                      >
                        {dm.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      </Box>

      {/* ── Perspective grid — internal scroll, aligned to search bar width ── */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 4 }}>
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>

          {/* Pinned */}
          {pinnedDms.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <PushPinIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>
                  Pinned
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
                {pinnedDms.map((dm) => (
                  <Grid item xs={12} sm={6} key={dm.id}>
                    <PerspectiveTile
                      dm={dm}
                      domainName={domainLabel(dm.domain)}
                      isPinned
                      onTogglePin={() => togglePinPerspective(dm.id)}
                      onClick={() => navigateToWorkspace(dm.domain, dm.id)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* By domain — or, without the domain layer, the whole catalog (#464) */}
          {Object.entries(perspectivesByDomain).map(([domainId, dms]) => (
            <Box key={domainId} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, mb: 0.5 }}>
                {domainsOn ? (domainNameMap[domainId] || domainId) : 'All perspectives'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2.5}>
                {dms.map((dm) => (
                  <Grid item xs={12} sm={6} key={dm.id}>
                    <PerspectiveTile
                      dm={dm}
                      domainName={domainLabel(domainId)}
                      isPinned={pinnedPerspectives.includes(dm.id)}
                      onTogglePin={() => togglePinPerspective(dm.id)}
                      onClick={() => navigateToWorkspace(dm.domain, dm.id)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {allPerspectives.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                No perspectives available. Make sure the backend is running.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================
// PerspectiveTile
// ============================================================

function PerspectiveTile({
  dm,
  domainName,
  isPinned,
  onTogglePin,
  onClick,
}: {
  dm: Perspective;
  domainName?: string;
  isPinned: boolean;
  onTogglePin: () => void;
  onClick: () => void;
}) {
  const MAX_METRIC_CHIPS = 3;
  const keyMetrics = dm.keyMetrics || [];
  const visibleMetrics = keyMetrics.slice(0, MAX_METRIC_CHIPS);
  const extraCount = keyMetrics.length - MAX_METRIC_CHIPS;

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: 4,
          borderColor: 'primary.main',
        },
      }}
    >
      <Tooltip title={isPinned ? 'Unpin' : 'Pin'}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: isPinned ? 'secondary.main' : 'text.disabled',
          }}
        >
          {isPinned ? (
            <PushPinIcon sx={{ fontSize: 18 }} />
          ) : (
            <PushPinOutlinedIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Tooltip>

      <CardActionArea onClick={onClick} sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
        <CardContent sx={{ pr: 5, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {dm.name}
          </Typography>
          {domainName && (
            <Chip
              label={domainName}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11, mb: 1 }}
            />
          )}
          {dm.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 1,
                fontSize: 13,
              }}
            >
              {dm.description}
            </Typography>
          )}
          {visibleMetrics.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {visibleMetrics.map((m) => (
                <Chip
                  key={m}
                  label={toDisplayName(m)}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    color: 'text.secondary',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ))}
              {extraCount > 0 && (
                <Chip
                  label={`+${extraCount} more`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    color: 'text.disabled',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
